import { memo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { CalendarCheck2, CheckCircle2, ClipboardList, Loader2 } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

import { VIRTUALIZE_JOBS_ABOVE } from '../config'
import { useRealtimeStore, type SelectionNoticeKind } from '../store/realtime-store'
import type { Job, JobsTab, Worker } from '../types'
import type { JobDisplayStatus } from '../utils/job-status'
import { JobCard } from './JobCard'

export interface JobsPanelProps {
  tab: JobsTab
  onTabChange: (tab: JobsTab) => void
  lists: Record<JobsTab, Job[]>
  workersById: Record<string, Worker>
  displayById: Map<string, JobDisplayStatus>
  nearbyCountById: Map<string, number>
  /** The one selected job (shared with the map and timeline). */
  selectedJobId: string | null
  highlightedJobs: Record<string, number>
  timeZone: string
  now: number
  isLoading: boolean
  /** Assignments still loading: Unassigned / Late counts and lists are not final yet. */
  staffingLoading?: boolean
  canForceRemove: boolean
  /** Notice for the selected job (removed / completed by another admin). */
  selectionNotice: { jobId: string; kind: SelectionNoticeKind } | null
  onDismissNotice: () => void
  onToggle: (jobId: string) => void
  onAssign: (jobId: string) => void
  onRemoveWorker: (jobId: string, workerId: string) => void
  className?: string
}

const EMPTY_COPY: Record<JobsTab, { title: string; description: string; icon: typeof ClipboardList }> = {
  unassigned: { title: 'Every job has a worker', description: 'New unassigned bookings will appear here instantly.', icon: CheckCircle2 },
  today: { title: 'No jobs today', description: 'Nothing is scheduled for this day yet.', icon: CalendarCheck2 },
  late: { title: 'Nobody is running late', description: 'Late arrivals will show up here with how late they are.', icon: CheckCircle2 },
}

const TAB_LABEL: Record<JobsTab, string> = { unassigned: 'Unassigned', today: 'Today', late: 'Late' }

function JobsList(props: Omit<JobsPanelProps, 'tab' | 'onTabChange' | 'lists' | 'isLoading' | 'staffingLoading' | 'className'> & { jobs: Job[] }) {
  const { jobs, workersById, displayById, nearbyCountById, selectedJobId, highlightedJobs, timeZone, now, canForceRemove, selectionNotice, onDismissNotice, onToggle, onAssign, onRemoveWorker } = props
  // Hover / row flashes only matter for the selected card; other cards never see them change.
  const hoveredWorkerId = useRealtimeStore((state) => state.hoveredWorkerId)
  const flashedRows = useRealtimeStore((state) => state.flashedRows)
  const setHoveredWorker = useRealtimeStore((state) => state.setHoveredWorker)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const virtualize = jobs.length > VIRTUALIZE_JOBS_ABOVE
  const virtualizer = useVirtualizer({
    count: virtualize ? jobs.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 96,
    overscan: 6,
  })

  const renderCard = (job: Job) => {
    const display = displayById.get(job.id)
    if (!display) return null
    const isSelected = selectedJobId === job.id
    return (
      <JobCard
        job={job}
        display={display}
        workersById={workersById}
        nearbyCount={nearbyCountById.get(job.id) ?? 0}
        isSelected={isSelected}
        highlightToken={highlightedJobs[job.id] ?? 0}
        timeZone={timeZone}
        now={now}
        canForceRemove={canForceRemove}
        hoveredWorkerId={isSelected ? hoveredWorkerId : null}
        flashedRows={isSelected ? flashedRows : undefined}
        notice={isSelected && selectionNotice?.jobId === job.id ? selectionNotice.kind : null}
        onDismissNotice={onDismissNotice}
        onToggle={onToggle}
        onAssign={onAssign}
        onRemoveWorker={onRemoveWorker}
        onHoverWorker={setHoveredWorker}
      />
    )
  }

  if (!virtualize) {
    return (
      <div ref={scrollRef} role="list" data-jobs-scroller="" className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
        {jobs.map((job) => <div key={job.id}>{renderCard(job)}</div>)}
      </div>
    )
  }

  return (
    <div ref={scrollRef} role="list" data-jobs-scroller="" className="min-h-0 flex-1 overflow-y-auto p-2">
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((item) => (
          <div
            key={jobs[item.index].id}
            ref={virtualizer.measureElement}
            data-index={item.index}
            className="absolute left-0 top-0 w-full pb-2"
            style={{ transform: `translateY(${item.start}px)` }}
          >
            {renderCard(jobs[item.index])}
          </div>
        ))}
      </div>
    </div>
  )
}

export const JobsPanel = memo(function JobsPanel(props: JobsPanelProps) {
  const { tab, onTabChange, lists, isLoading, staffingLoading = false, className, ...rest } = props
  const jobs = lists[tab]
  /** Which tabs depend on assignments (Today lists every job; its cards show their own loading rows). */
  const pendingTab = (key: JobsTab) => staffingLoading && key !== 'today'
  const showStaffingNote = staffingLoading && (pendingTab(tab) || jobs.length === 0)
  const empty = EMPTY_COPY[tab]
  const EmptyIcon = empty.icon

  return (
    <section
      aria-label="Jobs"
      className={cn('flex min-h-0 flex-col overflow-hidden rounded-xl border border-[#dfeae3] bg-white', className)}
    >
      <div className="border-b border-[#dfeae3] p-2">
        <Tabs value={tab} onValueChange={(value) => onTabChange(value as JobsTab)}>
          <TabsList className="grid h-9 w-full grid-cols-[1.45fr_1fr_1fr] gap-0.5">
            {(Object.keys(TAB_LABEL) as JobsTab[]).map((key) => (
              <TabsTrigger
                key={key}
                value={key}
                className="px-1 text-[11px] data-[state=active]:border-[#169b62] data-[state=active]:bg-[#169b62] data-[state=active]:hover:bg-[#169b62]"
              >
                {TAB_LABEL[key]}
                <span
                  className={cn(
                    'ml-0.5 rounded-full px-1 text-[10px] font-bold tabular-nums',
                    key === 'late' && lists.late.length > 0 ? 'bg-red-100 text-red-700' : 'bg-[#e8f4ee] text-[#355445]',
                    key === tab && 'bg-white/90',
                  )}
                >
                  {isLoading || pendingTab(key) ? '–' : lists[key].length}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <div className="space-y-2 p-2" aria-busy="true" aria-label="Loading jobs">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-[92px] w-full rounded-lg" />
          ))}
        </div>
      ) : jobs.length === 0 && showStaffingNote ? (
        <div className="min-h-0 flex-1 space-y-2 p-2" aria-hidden="true">
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} className="h-[92px] w-full rounded-lg" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e8f4ee] text-[#169b62]">
            <EmptyIcon className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="text-sm font-semibold text-[#20352b]">{empty.title}</p>
          <p className="max-w-[220px] text-xs text-[#718078]">{empty.description}</p>
        </div>
      ) : (
        <JobsList jobs={jobs} {...rest} />
      )}

      {!isLoading && showStaffingNote ? (
        <div role="status" className="flex items-center gap-2 border-t border-[#eef3ef] px-3 py-2 text-[11px] text-[#718078]">
          <Loader2 className="h-3.5 w-3.5 shrink-0 motion-safe:animate-spin" aria-hidden="true" />
          Loading assignments…
        </div>
      ) : null}
    </section>
  )
})
